// =============================================================================
// FILE: src/pages/healthcare/patient/modalComponents/supplements/RegimenEditorDrawer.tsx
// =============================================================================

import React, { useState, useEffect, useCallback } from "react";
import { Drawer, Button, Input, Select, Tag, Space, Typography, Empty, Modal, Tooltip, Collapse, Row, Col, Spin, Badge } from "antd";
import { SaveOutlined, PlusOutlined, CloseOutlined, DragOutlined, CaretDownOutlined, CaretRightOutlined, ArrowRightOutlined } from "@ant-design/icons";
import { DndContext, closestCenter, PointerSensor, KeyboardSensor, useSensor, useSensors, DragEndEvent } from "@dnd-kit/core";
import { SortableContext, useSortable, sortableKeyboardCoordinates, verticalListSortingStrategy, arrayMove } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import DetailDrawer from "../../../../../components/shared/DetailDrawer";
import { Product } from "../../../../../types/inventory/ProductTypes";
import { SupplementRegimen, SupplementRegimenItem, EditorItem, TimingSlot, ItemCategory, CreateRegimenPayload } from "../../../../../types/healthcare/SupplementTypes";
import { createRegimen, updateRegimen, addRegimenItem, updateRegimenItem, removeRegimenItem, reorderRegimenItems } from "../../../../../api/healthcare/SupplementApi";
import { useNotifications } from "../../../../../context/NotificationContext";
import { getSessionUser } from "../../../../../utils/auth/sessionUser";
import { Company } from "../../../../../types/admin/CompanyTypes";

const { Text } = Typography;
const { TextArea } = Input;

// ─── Constants ────────────────────────────────────────────────────────────────

export const TIMINGS: TimingSlot[] = ["morning", "lunch", "dinner", "night", "other"];

const TIMING_META: Record<TimingSlot, { label: string; icon: string }> = {
	morning: { label: "Morning", icon: "☀️" },
	lunch: { label: "Lunch", icon: "🌤" },
	dinner: { label: "Dinner", icon: "🌙" },
	night: { label: "Night", icon: "⭐" },
	other: { label: "Other", icon: "🕐" },
};

const CATEGORIES: ItemCategory[] = ["ESSENTIAL", "STRONGLY RECOMMENDED", "BENEFICIAL", "CURRENT - CONTINUE", "CURRENT - DISCONTINUE"];

const cleanHtml = (s?: string) =>
	(s || "")
		.replace(/<[^>]*>/g, "")
		.replace(/&amp;/g, "&")
		.replace(/&lt;/g, "<")
		.replace(/&gt;/g, ">")
		.replace(/&quot;/g, '"')
		.replace(/&#x27;/g, "'")
		.replace(/&nbsp;/g, " ")
		.trim();

const toEditorItem = (item: SupplementRegimenItem): EditorItem => ({
	id: item.id,
	timing: item.timing,
	product_name: item.product_name,
	full_name: item.full_name,
	product_url: item.product_url,
	dosage: item.dosage,
	instructions: item.instructions,
	category: item.category,
	description: item.description,
	rationale: item.rationale,
	sort_order: item.sort_order,
	is_active: item.is_active,
	_key: `item-${item.id}`,
});

const fromProduct = (p: Product, timing: TimingSlot): EditorItem => ({
	timing,
	product_name: p.name,
	full_name: p.name,
	product_url: p.custom_fields?.link || (p.shopify_handle ? `https://mygene.com.au/products/${p.shopify_handle}` : undefined),
	dosage: p.custom_fields?.dosage || "",
	instructions: p.custom_fields?.frequency || "",
	category: "BENEFICIAL",
	description: cleanHtml(p.description),
	rationale: "",
	sort_order: 0,
	is_active: true,
	_key: `new-${Date.now()}-${Math.random()}`,
});

// ─── Sortable Item Card ───────────────────────────────────────────────────────

interface ItemCardProps {
	item: EditorItem;
	timing: TimingSlot;
	expanded: boolean;
	onToggle: () => void;
	onUpdate: (field: keyof EditorItem, val: any) => void;
	onMove: (to: TimingSlot) => void;
	onRemove: () => void;
}

const SortableItemCard: React.FC<ItemCardProps> = ({ item, timing, expanded, onToggle, onUpdate, onMove, onRemove }) => {
	const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: item._key });

	const cardStyle: React.CSSProperties = {
		transform: CSS.Transform.toString(transform),
		transition,
		opacity: isDragging ? 0.4 : 1,
		border: "1px solid var(--mg-border)",
		borderRadius: 8,
		padding: "12px 14px",
		marginBottom: 8,
		background: "var(--mg-bg-elevated)",
	};

	return (
		<div ref={setNodeRef} style={cardStyle}>
			{/* Title row — full_name is what appears in the patient-facing modal */}
			<div style={{ display: "flex", alignItems: "flex-start", gap: 8, marginBottom: 10 }}>
				<span
					{...attributes}
					{...listeners}
					style={{ cursor: "grab", color: "var(--mg-text-disabled)", paddingTop: 26, flexShrink: 0 }}
				>
					<DragOutlined />
				</span>
				<div style={{ flex: 1, minWidth: 0 }}>
					<Text
						type="secondary"
						style={{ fontSize: 10, display: "block", marginBottom: 3, fontWeight: 600, letterSpacing: "0.06em", textTransform: "uppercase" }}
					>
						Supplement Title
					</Text>
					<Input
						value={item.full_name}
						onChange={(e) => onUpdate("full_name", e.target.value)}
						placeholder="Display name shown in patient view"
						style={{ fontWeight: 500 }}
					/>
				</div>
				<div style={{ display: "flex", gap: 2, paddingTop: 22, flexShrink: 0 }}>
					<Tooltip title={expanded ? "Collapse details" : "Expand details"}>
						<Button
							type="text"
							size="small"
							icon={expanded ? <CaretDownOutlined /> : <CaretRightOutlined />}
							onClick={onToggle}
						/>
					</Tooltip>
					<Tooltip title="Remove">
						<Button type="text" size="small" danger icon={<CloseOutlined />} onClick={onRemove} />
					</Tooltip>
				</div>
			</div>

			{/* Category */}
			<div style={{ marginBottom: 10 }}>
				<Select
					value={item.category}
					onChange={(v) => onUpdate("category", v)}
					options={CATEGORIES.map((c) => ({ label: c, value: c }))}
					style={{ width: "100%" }}
				/>
			</div>

			{/* Dosage + Instructions — always visible quick-edit */}
			<div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
				<div>
					<Text type="secondary" style={{ fontSize: 10, display: "block", marginBottom: 3, fontWeight: 600, letterSpacing: "0.06em", textTransform: "uppercase" }}>
						Dosage
					</Text>
					<Input
						placeholder="e.g. 2 capsules"
						value={item.dosage || ""}
						onChange={(e) => onUpdate("dosage", e.target.value)}
					/>
				</div>
				<div>
					<Text type="secondary" style={{ fontSize: 10, display: "block", marginBottom: 3, fontWeight: 600, letterSpacing: "0.06em", textTransform: "uppercase" }}>
						Instructions
					</Text>
					<Input
						placeholder="e.g. With food"
						value={item.instructions || ""}
						onChange={(e) => onUpdate("instructions", e.target.value)}
					/>
				</div>
			</div>

			{/* Expanded details */}
			{expanded && (
				<div style={{ borderTop: "1px solid var(--mg-border)", paddingTop: 12, marginTop: 12 }}>
					{/* Internal reference name */}
					<div style={{ marginBottom: 10 }}>
						<Text type="secondary" style={{ fontSize: 10, display: "block", marginBottom: 3, fontWeight: 600, letterSpacing: "0.06em", textTransform: "uppercase" }}>
							Internal Reference Name
						</Text>
						<Input
							placeholder="Internal product name or SKU"
							value={item.product_name}
							onChange={(e) => onUpdate("product_name", e.target.value)}
						/>
					</div>

					{/* Product URL */}
					<div style={{ marginBottom: 10 }}>
						<Text type="secondary" style={{ fontSize: 10, display: "block", marginBottom: 3, fontWeight: 600, letterSpacing: "0.06em", textTransform: "uppercase" }}>
							Product URL
						</Text>
						<Input
							placeholder="https://…"
							value={item.product_url || ""}
							onChange={(e) => onUpdate("product_url", e.target.value)}
						/>
					</div>

					{/* Description */}
					<div style={{ marginBottom: 10 }}>
						<Text type="secondary" style={{ fontSize: 10, display: "block", marginBottom: 3, fontWeight: 600, letterSpacing: "0.06em", textTransform: "uppercase" }}>
							Description
						</Text>
						<TextArea
							placeholder="Product description"
							rows={2}
							value={item.description || ""}
							onChange={(e) => onUpdate("description", e.target.value)}
						/>
					</div>

					{/* Rationale */}
					<div style={{ marginBottom: 10 }}>
						<Text type="secondary" style={{ fontSize: 10, display: "block", marginBottom: 3, fontWeight: 600, letterSpacing: "0.06em", textTransform: "uppercase" }}>
							Rationale (Gene-based)
						</Text>
						<TextArea
							placeholder="Gene-based rationale for this supplement"
							rows={2}
							value={item.rationale || ""}
							onChange={(e) => onUpdate("rationale", e.target.value)}
						/>
					</div>

					{/* Move to + open link */}
					<div style={{ display: "flex", alignItems: "center", gap: 8, paddingTop: 4 }}>
						<Text type="secondary" style={{ fontSize: 12 }}>Move to:</Text>
						<Select
							placeholder="Select timing…"
							value={undefined}
							onChange={(v) => onMove(v as TimingSlot)}
							options={TIMINGS.filter((t) => t !== timing).map((t) => ({
								label: `${TIMING_META[t].icon} ${TIMING_META[t].label}`,
								value: t,
							}))}
							style={{ width: 145 }}
						/>
						{item.product_url && (
							<Button type="link" size="small" href={item.product_url} target="_blank" style={{ padding: 0 }}>
								Open link →
							</Button>
						)}
					</div>
				</div>
			)}
		</div>
	);
};

// ─── Timing Column ────────────────────────────────────────────────────────────

interface ColProps {
	timing: TimingSlot;
	items: EditorItem[];
	expanded: Set<string>;
	onToggleExpand: (key: string) => void;
	onDragEnd: (timing: TimingSlot, e: DragEndEvent) => void;
	onUpdate: (timing: TimingSlot, key: string, field: keyof EditorItem, val: any) => void;
	onMove: (key: string, from: TimingSlot, to: TimingSlot) => void;
	onRemove: (timing: TimingSlot, key: string) => void;
	onAdd: (timing: TimingSlot) => void;
	sensors: ReturnType<typeof useSensors>;
}

const TimingColumn: React.FC<ColProps> = ({ timing, items, expanded, onToggleExpand, onDragEnd, onUpdate, onMove, onRemove, onAdd, sensors }) => {
	const meta = TIMING_META[timing];
	return (
		<div style={{ minWidth: 340, width: 340, display: "flex", flexDirection: "column" }}>
			{/* Column header */}
			<div
				style={{
					display: "flex",
					alignItems: "center",
					justifyContent: "space-between",
					marginBottom: 12,
					padding: "8px 0",
					borderBottom: "2px solid var(--mg-border)",
				}}
			>
				<Space size={8}>
					<span style={{ fontSize: 18 }}>{meta.icon}</span>
					<Text strong style={{ fontSize: 14 }}>{meta.label}</Text>
					<Badge count={items.length} style={{ backgroundColor: "var(--mg-text-secondary)" }} />
				</Space>
				<Button type="dashed" icon={<PlusOutlined />} onClick={() => onAdd(timing)}>
					Add
				</Button>
			</div>

			<DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={(e) => onDragEnd(timing, e)}>
				<SortableContext items={items.map((i) => i._key)} strategy={verticalListSortingStrategy}>
					{items.length === 0 ? (
						<div
							style={{
								border: "1px dashed var(--mg-border)",
								borderRadius: 8,
								padding: 24,
								textAlign: "center",
								minHeight: 100,
								display: "flex",
								alignItems: "center",
								justifyContent: "center",
							}}
						>
							<Text type="secondary" style={{ fontSize: 13 }}>No supplements yet</Text>
						</div>
					) : (
						<div style={{ flex: 1 }}>
							{items.map((item) => (
								<SortableItemCard
									key={item._key}
									item={item}
									timing={timing}
									expanded={expanded.has(item._key)}
									onToggle={() => onToggleExpand(item._key)}
									onUpdate={(f, v) => onUpdate(timing, item._key, f, v)}
									onMove={(to) => onMove(item._key, timing, to)}
									onRemove={() => onRemove(timing, item._key)}
								/>
							))}
						</div>
					)}
				</SortableContext>
			</DndContext>
		</div>
	);
};

// ─── Main Drawer ──────────────────────────────────────────────────────────────

interface RegimenEditorDrawerProps {
	regimen: SupplementRegimen | (Omit<SupplementRegimen, "id" | "uuid"> & { id: 0; uuid: "" });
	catalog: Product[];
	onClose: () => void;
	onSaved: () => void;
	company: Company;
}

const RegimenEditorDrawer: React.FC<RegimenEditorDrawerProps> = ({ regimen, catalog, onClose, onSaved, company }) => {
	const user = getSessionUser();
	const { showSuccess, showError } = useNotifications();

	const isNew = regimen.id === 0;

	const [name, setName] = useState(regimen.name || "New Supplement Regimen");
	const [notes, setNotes] = useState(regimen.notes || "");
	const [summary, setSummary] = useState(regimen.summary || "");
	const [status, setStatus] = useState<string>(regimen.status || "draft");
	const [slots, setSlots] = useState<Record<TimingSlot, EditorItem[]>>({
		morning: [],
		lunch: [],
		dinner: [],
		night: [],
		other: [],
	});
	const [originalItems, setOriginalItems] = useState<Map<number, SupplementRegimenItem>>(new Map());
	const [saving, setSaving] = useState(false);
	const [expanded, setExpanded] = useState<Set<string>>(new Set());

	// Catalog picker
	const [addSlot, setAddSlot] = useState<TimingSlot | null>(null);
	const [selectedPk, setSelectedPk] = useState<string[]>([]);

	const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }), useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }));

	// ── Init ───────────────────────────────────────────────────────────────────

	useEffect(() => {
		const orig = new Map<number, SupplementRegimenItem>();
		const init: Record<TimingSlot, EditorItem[]> = {
			morning: [],
			lunch: [],
			dinner: [],
			night: [],
			other: [],
		};
		TIMINGS.forEach((t) => {
			(regimen.regimen?.[t] || []).forEach((item) => {
				init[t].push(toEditorItem(item));
				orig.set(item.id, item);
			});
		});
		setSlots(init);
		setOriginalItems(orig);
	}, [regimen]);

	// ── Slot mutations ─────────────────────────────────────────────────────────

	const updateItem = useCallback(
		(timing: TimingSlot, key: string, field: keyof EditorItem, val: any) =>
			setSlots((prev) => ({
				...prev,
				[timing]: prev[timing].map((i) => (i._key === key ? { ...i, [field]: val } : i)),
			})),
		[],
	);

	const removeItem = useCallback((timing: TimingSlot, key: string) => setSlots((prev) => ({ ...prev, [timing]: prev[timing].filter((i) => i._key !== key) })), []);

	const moveItem = useCallback(
		(key: string, from: TimingSlot, to: TimingSlot) =>
			setSlots((prev) => {
				const item = prev[from].find((i) => i._key === key);
				if (!item) return prev;
				const moved = { ...item, timing: to, _key: `moved-${Date.now()}` };
				return {
					...prev,
					[from]: prev[from].filter((i) => i._key !== key),
					[to]: [...prev[to], moved],
				};
			}),
		[],
	);

	const handleDragEnd = useCallback((timing: TimingSlot, { active, over }: DragEndEvent) => {
		if (!over || active.id === over.id) return;
		setSlots((prev) => {
			const arr = prev[timing];
			const from = arr.findIndex((i) => i._key === active.id);
			const to = arr.findIndex((i) => i._key === over.id);
			if (from === -1 || to === -1) return prev;
			return { ...prev, [timing]: arrayMove(arr, from, to) };
		});
	}, []);

	const toggleExpand = useCallback((key: string) => {
		setExpanded((prev) => {
			const n = new Set(prev);
			n.has(key) ? n.delete(key) : n.add(key);
			return n;
		});
	}, []);

	// ── Catalog add ────────────────────────────────────────────────────────────

	const openCatalogPicker = (timing: TimingSlot) => {
		setAddSlot(timing);
		setSelectedPk([]);
	};

	const confirmAddFromCatalog = () => {
		if (!addSlot || selectedPk.length === 0) return;
		const newItems = selectedPk
			.map((name) => catalog.find((p) => p.name === name))
			.filter(Boolean)
			.map((p) => fromProduct(p!, addSlot));
		setSlots((prev) => ({ ...prev, [addSlot]: [...prev[addSlot], ...newItems] }));
		setAddSlot(null);
		setSelectedPk([]);
	};

	// ── Save ───────────────────────────────────────────────────────────────────

	const handleSave = async () => {
		if (!name.trim()) {
			showError("Regimen name is required");
			return;
		}
		setSaving(true);
		try {
			if (isNew) {
				const allItems = TIMINGS.flatMap((t) =>
					slots[t].map((item, j) => ({
						timing: t,
						product_name: item.product_name,
						full_name: item.full_name,
						product_url: item.product_url || undefined,
						dosage: item.dosage || undefined,
						instructions: item.instructions || undefined,
						category: item.category,
						description: item.description || undefined,
						rationale: item.rationale || undefined,
						sort_order: j,
					})),
				);
				await createRegimen({
					patient_id: regimen.patient_id,
					practitioner_id: user.id,
					name: name.trim(),
					notes: notes || undefined,
					summary: summary || undefined,
					status: status as any,
					items: allItems.length > 0 ? allItems : undefined,
					company: company.name,
				} as CreateRegimenPayload);
			} else {
				const uuid = (regimen as SupplementRegimen).uuid;

				// Metadata
				await updateRegimen(uuid, { name: name.trim(), notes, summary, status });

				// All current items flat with updated sort_order
				const allCurrent = TIMINGS.flatMap((t) => slots[t].map((item, j) => ({ ...item, timing: t, sort_order: j })));
				const currentIds = new Set(allCurrent.filter((i) => i.id).map((i) => i.id!));

				// Delete removed
				for (const [id] of originalItems) {
					if (!currentIds.has(id)) await removeRegimenItem(uuid, id);
				}
				// Add new
				for (const item of allCurrent) {
					if (!item.id) {
						await addRegimenItem(uuid, {
							timing: item.timing,
							product_name: item.product_name,
							full_name: item.full_name,
							product_url: item.product_url || undefined,
							dosage: item.dosage || undefined,
							instructions: item.instructions || undefined,
							category: item.category,
							description: item.description || undefined,
							rationale: item.rationale || undefined,
							sort_order: item.sort_order,
						});
					}
				}
				// Update changed (includes name fields so edits to title are persisted)
				for (const item of allCurrent) {
					if (!item.id) continue;
					const orig = originalItems.get(item.id);
					if (!orig) continue;
					if (
						item.timing !== orig.timing ||
						item.product_name !== orig.product_name ||
						item.full_name !== orig.full_name ||
						item.dosage !== (orig.dosage || "") ||
						item.instructions !== (orig.instructions || "") ||
						item.category !== orig.category ||
						item.description !== (orig.description || "") ||
						item.rationale !== (orig.rationale || "") ||
						item.sort_order !== orig.sort_order
					) {
						await updateRegimenItem(uuid, item.id, {
							timing: item.timing,
							product_name: item.product_name,
							full_name: item.full_name,
							dosage: item.dosage,
							instructions: item.instructions,
							category: item.category,
							description: item.description,
							rationale: item.rationale,
							sort_order: item.sort_order,
						});
					}
				}
				// Reorder
				const reorderPayload = allCurrent.filter((i) => i.id).map((i) => ({ id: i.id!, sort_order: i.sort_order }));
				if (reorderPayload.length > 0) await reorderRegimenItems(uuid, reorderPayload);
			}

			showSuccess("Regimen saved successfully");
			onSaved();
			onClose();
		} catch (e: any) {
			showError(e?.response?.data?.message || "Failed to save regimen");
		} finally {
			setSaving(false);
		}
	};

	const totalItems = TIMINGS.reduce((n, t) => n + slots[t].length, 0);

	return (
		<>
			<DetailDrawer
				open
				onClose={onClose}
				width="95vw"
				title={
					<Input
						value={name}
						onChange={(e) => setName(e.target.value)}
						variant="borderless"
						placeholder="Regimen name"
						style={{ fontSize: 16, fontWeight: 600, padding: 0 }}
					/>
				}
				subtitle={
					<Space size={8}>
						<Tag color={regimen.type === "ai_generated" ? "purple" : "blue"}>
							{regimen.type === "ai_generated" ? "AI Generated" : "Manual"}
						</Tag>
						<Text type="secondary" style={{ fontSize: 12 }}>
							{totalItems} supplement{totalItems !== 1 ? "s" : ""}
						</Text>
					</Space>
				}
				headerActions={
					<>
						<Select
							value={status}
							onChange={setStatus}
							options={[
								{ label: "Draft", value: "draft" },
								{ label: "Active", value: "active" },
								{ label: "Archived", value: "archived" },
							]}
							style={{ width: 110 }}
						/>
						<Button type="primary" icon={<SaveOutlined />} onClick={handleSave} loading={saving}>
							Save Regimen
						</Button>
					</>
				}
				footer={
					<Button onClick={onClose} disabled={saving}>
						Cancel
					</Button>
				}
			>
				{/* Description */}
				<div style={{ marginBottom: 16 }}>
					<Text type="secondary" style={{ fontSize: 11, display: "block", marginBottom: 6, fontWeight: 600, letterSpacing: "0.06em", textTransform: "uppercase" }}>
						Description
					</Text>
					<TextArea
						placeholder="Description shown to the patient at the top of this regimen"
						rows={3}
						value={summary}
						onChange={(e) => setSummary(e.target.value)}
					/>
				</div>

				{/* Practitioner Notes */}
				<div style={{ marginBottom: 20 }}>
					<Text type="secondary" style={{ fontSize: 11, display: "block", marginBottom: 6, fontWeight: 600, letterSpacing: "0.06em", textTransform: "uppercase" }}>
						Practitioner Notes
					</Text>
					<TextArea
						placeholder="Optional notes for this regimen"
						rows={2}
						value={notes}
						onChange={(e) => setNotes(e.target.value)}
					/>
				</div>

				{/* Timing columns — horizontal scroll */}
				<div style={{ overflow: "auto" }}>
					<div style={{ display: "flex", gap: 12, alignItems: "flex-start", minWidth: "fit-content", paddingBottom: 8 }}>
						{TIMINGS.map((timing) => (
							<TimingColumn
								key={timing}
								timing={timing}
								items={slots[timing]}
								expanded={expanded}
								sensors={sensors}
								onToggleExpand={toggleExpand}
								onDragEnd={handleDragEnd}
								onUpdate={updateItem}
								onMove={moveItem}
								onRemove={removeItem}
								onAdd={openCatalogPicker}
							/>
						))}
					</div>
				</div>
			</DetailDrawer>

			{/* Catalog picker modal */}
			<Modal
				title={`Add supplements — ${addSlot ? TIMING_META[addSlot].label : ""}`}
				open={addSlot !== null}
				onCancel={() => {
					setAddSlot(null);
					setSelectedPk([]);
				}}
				width={720}
				destroyOnClose
				footer={[
					<Button
						key="c"
						onClick={() => {
							setAddSlot(null);
							setSelectedPk([]);
						}}
					>
						Cancel
					</Button>,
					<Button key="a" type="primary" onClick={confirmAddFromCatalog} disabled={selectedPk.length === 0}>
						Add {selectedPk.length > 0 ? `(${selectedPk.length})` : ""} Supplement{selectedPk.length !== 1 ? "s" : ""}
					</Button>,
				]}
			>
				<div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
					<Text type="secondary" style={{ fontSize: 13 }}>
						Search and select supplements from the catalog
					</Text>
					<Select
						mode="multiple"
						style={{ width: "100%" }}
						value={selectedPk}
						onChange={setSelectedPk}
						placeholder="Search for supplements and select from results…"
						showSearch
						maxTagCount="responsive"
						filterOption={(input, option) => (option?.label ?? "").toLowerCase().includes(input.toLowerCase())}
						options={catalog.map((p) => ({ label: p.name, value: p.name }))}
						optionFilterProp="label"
					/>
					{selectedPk.length > 0 && (
						<div style={{ borderTop: "1px solid var(--mg-border)", paddingTop: 12 }}>
							<Text type="secondary" style={{ fontSize: 12, display: "block", marginBottom: 8 }}>
								Selected ({selectedPk.length}):
							</Text>
							<Space direction="vertical" style={{ width: "100%" }} size={4}>
								{selectedPk.map((name) => (
									<div
										key={name}
										style={{
											padding: "8px 12px",
											borderRadius: 6,
											border: "1px solid var(--mg-border)",
											background: "var(--mg-bg-subtle)",
											display: "flex",
											justifyContent: "space-between",
											alignItems: "center",
										}}
									>
										<Text style={{ fontSize: 13 }}>{name}</Text>
										<Button
											type="text"
											danger
											size="small"
											icon={<CloseOutlined />}
											onClick={() => setSelectedPk(selectedPk.filter((s) => s !== name))}
										/>
									</div>
								))}
							</Space>
						</div>
					)}
				</div>
			</Modal>
		</>
	);
};

export default RegimenEditorDrawer;
